import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Outlet, Link, useLocation, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Breadcrumbs,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useForumStore, useAuthStore } from '../../stores/root_store';

export const ForumPage = observer(() => {
  const location = useLocation();
  const params = useParams();
  const forumStore = useForumStore();
  const authStore = useAuthStore();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newSectionDescription, setNewSectionDescription] = useState('');

  const isManager = authStore.roles.includes('manager');

  // Fetch sections on mount
  useEffect(() => {
    if (authStore.initialized) {
      forumStore.fetchSections();
    }
  }, [authStore.initialized, forumStore]);

  // Determine current section from URL
  const currentSectionSlug = params.sectionSlug || null;
  const currentPostSlug = params.postSlug || null;
  const currentSection = currentSectionSlug
    ? forumStore.getSectionBySlug(currentSectionSlug)
    : null;

  // Determine if we're on the home page
  const isHome = location.pathname === '/assistant/forum' || location.pathname === '/assistant/forum/';

  // Get the tab value for the section tabs
  const getTabValue = () => {
    if (isHome) return 'home';
    return currentSectionSlug || 'home';
  };

  const handleCreateSection = async () => {
    if (!newSectionTitle.trim()) return;

    const section = await forumStore.createSection({
      title: newSectionTitle.trim(),
      description: newSectionDescription.trim() || undefined,
      order: forumStore.sections.length,
    });

    if (section) {
      setCreateDialogOpen(false);
      setNewSectionTitle('');
      setNewSectionDescription('');
    }
  };

  const handleCloseDialog = () => {
    setCreateDialogOpen(false);
    setNewSectionTitle('');
    setNewSectionDescription('');
    forumStore.clearErrors();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Forum
          </Typography>
          {isManager && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              size="small"
            >
              New Section
            </Button>
          )}
        </Box>

        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link to="/assistant/forum" style={{ textDecoration: 'none', color: 'inherit' }}>
            <Typography
              color={isHome ? 'text.primary' : 'text.secondary'}
              sx={{ '&:hover': { textDecoration: 'underline' } }}
            >
              Forum
            </Typography>
          </Link>
          {currentSection && (
            <Link
              to={`/assistant/forum/${currentSection.slug}`}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <Typography
                color={currentPostSlug ? 'text.secondary' : 'text.primary'}
                sx={{ '&:hover': { textDecoration: 'underline' } }}
              >
                {currentSection.title}
              </Typography>
            </Link>
          )}
          {currentPostSlug && forumStore.currentPost && (
            <Typography color="text.primary">
              {forumStore.currentPost.title}
            </Typography>
          )}
        </Breadcrumbs>
      </Box>

      {/* Section Tabs */}
      {forumStore.sectionsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : forumStore.sections.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" gutterBottom>
            No forum sections yet.
          </Typography>
          {isManager && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ mt: 2 }}
            >
              Create First Section
            </Button>
          )}
        </Box>
      ) : (
        <Tabs
          value={getTabValue()}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        >
          <Tab
            value="home"
            label="Recent"
            component={Link}
            to="/assistant/forum"
          />
          {forumStore.sections.map((section) => (
            <Tab
              key={section.uuid}
              value={section.slug}
              label={section.title}
              component={Link}
              to={`/assistant/forum/${section.slug}`}
            />
          ))}
        </Tabs>
      )}

      {/* Page Content */}
      {forumStore.sections.length > 0 && <Outlet />}

      {/* Create Section Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create Forum Section</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Section Title"
              fullWidth
              value={newSectionTitle}
              onChange={(e) => setNewSectionTitle(e.target.value)}
              disabled={forumStore.saving}
              required
              placeholder="e.g., General Discussion"
            />
            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              rows={2}
              value={newSectionDescription}
              onChange={(e) => setNewSectionDescription(e.target.value)}
              disabled={forumStore.saving}
              placeholder="What is this section for?"
            />
            {forumStore.saveError && (
              <Alert severity="error">{forumStore.saveError}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={forumStore.saving}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateSection}
            variant="contained"
            disabled={!newSectionTitle.trim() || forumStore.saving}
          >
            {forumStore.saving ? <CircularProgress size={20} /> : 'Create Section'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
});
